import { ArrowLeft, Check, Download, Heart, MessageSquare } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { toast } from "sonner";
import { dashboardApi } from "../api";
import { DeliverableFrame, PrismicMark } from "../components/Deliverables";
import RequestTimeline from "../components/RequestTimeline";
import { HeroSkeleton, useSlowEnoughToShow } from "../components/Skeleton";
import type { Attachment, FeedbackRating, Handoff } from "../types";
import { formatCredits, formatDate } from "../utils";
import { useServiceText } from "../service-text";
import { useT } from "../i18n";

/**
 * The hand-off.
 *
 * Everywhere else in this product, finished work is a row with a green pill on it.
 * That is an accounting view of a creative business. This screen exists so the
 * moment a client opens their delivery, they see the WORK — big, first, and
 * without a table around it. The credits and the dates are still here, underneath,
 * because this is also a portal about money; they are just no longer the headline.
 */
export default function DeliveryPage({ requestId }: { requestId: string }) {
  const t = useT();
  const st = useServiceText();
  const [data, setData] = useState<Handoff | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    dashboardApi.handoff(requestId)
      .then((result) => { if (live) setData(result); })
      .catch((err) => { if (live) setError(err instanceof Error ? err.message : t("error.loadDelivery")); });
    return () => { live = false; };
  }, [requestId]);

  if (error) {
    return (
      <div className="page-stack">
        <div className="empty-state">
          <PrismicMark size={38} />
          <h4>{t("delivery.unavailable")}</h4>
          <p>{error}</p>
          <Link className="button button-secondary" href="/work">{t("delivery.backToWork")}</Link>
        </div>
      </div>
    );
  }
  const showSkeleton = useSlowEnoughToShow(!data);

  if (!data) return showSkeleton
    ? <div className="page-stack"><HeroSkeleton label={t("delivery.loading")} /></div>
    : null;

  const { request, files } = data;
  const cover = files.find((file) => file.isCover) ?? files[0] ?? null;
  const rest = files.filter((file) => file.id !== cover?.id);
  const delivered = request.status === "completed";

  return (
    <div className="page-stack delivery-page">
      <Link className="back-link" href="/work"><ArrowLeft size={15} /> {t("delivery.allWork")}</Link>

      <header className="delivery-header">
        <span className="section-eyebrow">{st.category(request.service_category)}</span>
        <h2>{request.title}</h2>
        <p className="delivery-subhead">
          {delivered && request.completed_at
            ? <>Delivered {formatDate(request.completed_at)}{request.delivered_by ? ` by ${request.delivered_by}` : ""}</>
            : t("delivery.inProduction")}
        </p>
      </header>

      {/* The work, at the top, at size. */}
      <section className="delivery-hero">
        <DeliverableFrame attachment={cover} ratio="16 / 9" label="Nothing delivered yet" />
      </section>

      {rest.length > 0 && (
        <section className="delivery-grid">
          {rest.map((file) => <DeliveryTile key={file.id} file={file} />)}
        </section>
      )}

      {files.length > 0 && (
        <div className="delivery-actions">
          {/* One link per file rather than a zip: a per-request archive is a second
              streaming endpoint to get wrong, and most deliveries are one or two files. */}
          {files.map((file) => (
            <a key={file.id} className="button button-secondary" href={file.url} download={file.filename}>
              <Download size={16} /> {file.filename}
            </a>
          ))}
        </div>
      )}

      <section className="delivery-facts">
        <dl>
          <div><dt>{t("delivery.whatThisWas")}</dt><dd>{st.name(request.service_id, request.service_name)}{request.quantity > 1 ? ` × ${request.quantity}` : ""}</dd></div>
          <div><dt>{t("table.language")}</dt><dd>{request.language}</dd></div>
          <div><dt>{t("table.requestedBy")}</dt><dd>{request.requested_by ?? "—"}</dd></div>
          <div><dt>{request.final_credits !== null ? t("delivery.creditsUsed") : t("delivery.estimated")}</dt><dd>{formatCredits(request.credits)}</dd></div>
        </dl>
        {request.brief && <p className="delivery-brief"><strong>{t("delivery.theBrief")}</strong>{request.brief}</p>}
      </section>

      <div className="delivery-columns">
        <FeedbackPanel
          requestId={request.id}
          delivered={delivered}
          mine={data.mine}
          others={data.feedback.filter((entry) => entry.user_id !== data.mine?.user_id)}
        />
        <section className="request-history">
          <h4>{t("requests.history")}</h4>
          <RequestTimeline requestId={request.id} />
        </section>
      </div>
    </div>
  );
}

function DeliveryTile({ file }: { file: Attachment }) {
  const isImage = file.mime.startsWith("image/");
  return (
    <figure className="delivery-tile">
      {isImage
        ? <a href={file.url} target="_blank" rel="noreferrer"><DeliverableFrame attachment={file} ratio="4 / 3" /></a>
        : <DeliverableFrame attachment={file} ratio="4 / 3" />}
      <figcaption>{file.filename}</figcaption>
    </figure>
  );
}

/** The written form of a stored rating. An unknown value returns the raw string
 *  rather than a key: `t()` echoes an unknown key back, so a missing entry would
 *  otherwise print "feedback.loved" at a client. */
function ratingLabel(rating: string, t: (key: string) => string): string {
  const choice = CHOICES.find((c) => c.id === rating);
  return choice ? t(choice.labelKey) : rating;
}

/** `label` is a KEY, resolved at render — a module constant is built at import,
 *  before a reader has chosen a language. */
const CHOICES: Array<{ id: FeedbackRating; labelKey: string; icon: typeof Heart }> = [
  { id: "loved", labelKey: "feedback.loved", icon: Heart },
  { id: "good", labelKey: "feedback.good", icon: Check },
  { id: "changes", labelKey: "feedback.changes", icon: MessageSquare },
];

/**
 * The first satisfaction signal this business has ever had inside a system.
 *
 * One tap, no required words. A five-star scale with a comment box gets a 4% response
 * rate and tells you nothing; three buttons get answered. The note is there for the
 * client who wants to say more, and hidden until they pick something.
 */
function FeedbackPanel({
  requestId, delivered, mine, others,
}: {
  requestId: string;
  delivered: boolean;
  mine: Handoff["mine"];
  others: Handoff["feedback"];
}) {
  const t = useT();
  const [rating, setRating] = useState<FeedbackRating | null>(mine?.rating ?? null);
  const [note, setNote] = useState(mine?.note ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(Boolean(mine));

  // Work that is not delivered cannot be reacted to; the server refuses it too.
  if (!delivered) return null;

  async function send(next: FeedbackRating, withNote: string) {
    setSaving(true);
    try {
      await dashboardApi.sendFeedback(requestId, next, withNote.trim() || undefined);
      setRating(next);
      setSaved(true);
      toast.success("Thank you — the team will see this.");
    } catch (err) {
      // Do NOT leave the button looking selected if the save failed.
      setRating(mine?.rating ?? null);
      toast.error(err instanceof Error ? err.message : t("error.saveFeedback"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="feedback-panel">
      <h4>{t("delivery.howDidWeDo")}</h4>
      <div className="feedback-choices">
        {CHOICES.map(({ id, labelKey, icon: Icon }) => (
          <button
            key={id}
            type="button"
            disabled={saving}
            aria-pressed={rating === id}
            className={rating === id ? `feedback-choice is-${id} active` : `feedback-choice is-${id}`}
            onClick={() => send(id, note)}
          >
            <Icon size={17} /> {t(labelKey)}
          </button>
        ))}
      </div>

      {rating && (
        <div className="feedback-note">
          <label htmlFor="feedback-note">Anything you want the team to know? (optional)</label>
          <textarea
            id="feedback-note"
            rows={3}
            value={note}
            disabled={saving}
            onChange={(event) => setNote(event.target.value)}
            placeholder={rating === "changes" ? t("feedback.whatChanging") : t("feedback.whatWorked")}
          />
          <button className="button button-secondary" disabled={saving} onClick={() => send(rating, note)}>
            {saving ? t("common.saving") : t(saved ? "feedback.update" : "common.send")}
          </button>
        </div>
      )}

      {others.length > 0 && (
        <div className="feedback-others">
          <span>{t("delivery.alsoFromTeam")}</span>
          {others.map((entry) => (
            <p key={entry.user_id}>
              <strong>{entry.author ?? t("feedback.someone")}</strong> — {ratingLabel(entry.rating, t)}
              {entry.note ? `: "${entry.note}"` : ""}
            </p>
          ))}
        </div>
      )}
    </section>
  );
}
